import React from "react";
import { useSelector } from "react-redux";

import Button from "@cayuse/shared-react.components.button";
import { cloneDeep, isEqual, set } from "lodash";
import PropTypes from "prop-types";

import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";

import { withFlowableStatefulFormComponent } from "components/flowable/wrappers";
import { useDictionary } from "hooks";
import { tenantSelectors } from "store/selectors";

import { ProgramBlock } from "./components/ProgramBlock";
import { FundingProgramContextProvider } from "./FundingProgramContext";
import styles from "./styles.css";

/**
 * @typedef SavedProgram
 *
 * @prop {string} id - program id
 * @prop {?string} roundId - round id
 */

/**
 * @typedef SelectedProgram
 *
 * @property {{ item: Object; value: string; label: string; } | {}} program
 * @property {{ item: Object; value: string; label: string; } | null} round
 */

const /** @type SelectedProgram*/emptyItem = {
	program: {
		value: "",
	},
	round: null,
};

const FundingProgramComponent = props => {
	const {
		config,
		Components,
		forceValidations,
	} = props;

	const fundingSchemeDictionary = useSelector(tenantSelectors.useFundingSchemeDictionary);
	const dictionary = useDictionary(`${fundingSchemeDictionary ? "fundingScheme" : "fundingProgram"}.form`);

	const [ errors, setErrors ] = React.useState(/** @type {Record<number, Record<"round" | "program", string>>}[] */{});
	const [ lastSaved, setLastSaved ] = React.useState(/** @type SavedProgram[] */[]);
	const [ programs, setPrograms ] = React.useState(/** @type SelectedProgram[] */[ cloneDeep(emptyItem) ]);
	const [ customValidationErrors, setCustomValidationErrors ] = React.useState([]);

	const validate = () => {
		const value = isNonEmptyArray(config.value) ? config.value : [ { id: "" } ];

		const _errors = {};
		value.forEach((item, k) => {
			if (config.isRequired) {
				if (item.roundId?.length === 0) {
					set(_errors, `${k}.round`, dictionary.errors.emptyRound);
				}
				else if (!item.id) {
					set(_errors, `${k}.program`, dictionary.errors.emptyField);
				}
			}
		});
		setErrors(_errors);
	};

	const validateCustomValidations = () => {
		if (isNonEmptyArray(config.customValidations)) {
			const errors = config.customValidations
				.filter(validation => !validation.expression)
				.map(validation => validation.errorMsg);
			setCustomValidationErrors(errors);
		} else {
			setCustomValidationErrors([]);
		}
	};

	const formatValueAndSetState = () => {
		const /** @type SavedProgram[] */lastSaved = config.value;

		if (isNonEmptyArray(lastSaved)) {
			const /** @type SelectedProgram[] */nextPrograms = lastSaved.map((saved, index) => {
				const item = {
					program: {
						value: saved.id,
						label: "",
						item: {},
						hasRounds: false
					},
					round: "roundId" in saved ? {
						value: saved.roundId,
						label: "",
						item: {},
					} : null,
				};

				const current = programs[index];
				if (current?.program?.value === item.program.value) {
					item.program = current.program;
					if (current.round?.value === item.round?.value) {
						item.round = current.round;
					}
				}

				return item;
			});

			setPrograms(nextPrograms);
			setLastSaved(lastSaved);
		} else {
			setPrograms([ cloneDeep(emptyItem) ]);
			setLastSaved([]);
		}
	};

	React.useEffect(() => {
		formatValueAndSetState();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		formatValueAndSetState();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ config.value, config.id, config.customValidations ]);

	const addEmptyProgram = () => {
		onChange(
			programs.length,
			cloneDeep(emptyItem),
		);
	};

	const onChange = (index, value) => {
		let nextValue;
		// add new empty program
		if (index === programs.length) {
			nextValue = programs.concat([ value ]);
		}
		// remove program by index
		else if (!value) {
			nextValue = programs.filter((_, i) => i !== index);
		}
		// update program by index
		else {
			nextValue = programs.map((item, i) => i === index ? value : item);
		}

		setPrograms(nextValue);

		const /** @type SavedProgram[] */valueToSave = nextValue
			.map(it => {
				if (it.round) {
					return {
						id: it.program.value,
						roundId: it.round.value,
					};
				}

				return {
					id: it.program.value,
				};
			});

		if (!isEqual(lastSaved, valueToSave)) {
			props.onChange(valueToSave);
		}

		setCustomValidationErrors([]);
		setErrors({});
	};

	const onFocus = () => {
		props.dispatchFocusEvent();
		props.setTouched();
	};

	const onBLur = () => {
		props.dispatchBlurEvent();
		validate();
		validateCustomValidations();
	};

	const bem = props.bem("panel", { disabled: config.enabled === false });

	return  (
		<div className={bem()}>
			<div className={styles.wrapper}>
				<Components.label {...props} />

				<FundingProgramContextProvider
					config={config}
					programs={programs}
					onChange={onChange}
					onFocus={onFocus}
					onBlur={onBLur} 
					touched={props.isTouched()}
					errors={errors}
				>
					{programs.map((item, k) => (
						<>
							<ProgramBlock
								key={k}
								index={k}
								errors={errors[k]}
								touched={props.isTouched()}
								forceValidations={forceValidations}
							/>
						</>
					))}
				</FundingProgramContextProvider>

				<Button
					icon="plus"
					className={styles.add}
					onClick={addEmptyProgram}
					onFocus={onFocus}
					onBlur={props.dispatchBlurEvent}
					disabled={!config.enabled}
					data-testid="add-funding-program-button"
				>
					{dictionary.addFundingProgramButton}
				</Button>

				<div className={styles.helpSection}>
					<Components.description {...props} />
					<div role="alert">
						{(!forceValidations && !props.isTouched() && customValidationErrors.length > 0)
							? null
							: <>
								{customValidationErrors.map((error, i) => (
									<span key={i} className="flw__error-list__error-list-item"
										data-testid="error-list-item"
									>
										{error}
									</span>
								))}
							</>
						}
					</div>
				</div>
			</div>
		</div>
	);
};

export const FundingProgram = withFlowableStatefulFormComponent(FundingProgramComponent);

FundingProgramComponent.propTypes = {
	config: PropTypes.shape({
		isRequired: PropTypes.bool,
		enabled: PropTypes.bool,
		value: PropTypes.array,
		id: PropTypes.string,
	}).isRequired,
	isTouched: PropTypes.func.isRequired,
	onChange: PropTypes.func.isRequired,
	dispatchFocusEvent: PropTypes.func.isRequired,
	dispatchBlurEvent: PropTypes.func.isRequired,
	bem: PropTypes.func.isRequired,
	forceValidations: PropTypes.bool,
};
